import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { ToggleSwitch } from '@/shared/components/custom/toggle-switch/ToggleSwitch';
import { useOrganizations } from '@/shared/hooks/useOrganizations';
import { cn } from '@/core/lib/utils';
import {
  useUpdateLinkedCalendar,
  updateLinkedCalendarSchema,
  type LinkedCalendar,
  type UpdateLinkedCalendarInput,
} from '@/features/calendar/public';

/**
 * Preset hex swatches — the shared ColorPicker emits oklch design tokens, but
 * the backend contract requires `#RRGGBB` (spec deviation, decided at build).
 */
const PRESET_HEX_COLORS = [
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#84CC16',
  '#10B981',
  '#06B6D4',
  '#3B82F6',
  '#8B5CF6',
  '#D946EF',
  '#6B7280',
] as const;

interface LinkedCalendarScopeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  calendar: LinkedCalendar;
  connectionId: string;
}

export function LinkedCalendarScopeDialog({
  open,
  onOpenChange,
  calendar,
  connectionId,
}: LinkedCalendarScopeDialogProps) {
  const { t } = useTranslation('calendar');
  const updateCalendar = useUpdateLinkedCalendar();
  const { data: organizations = [] } = useOrganizations();

  const form = useForm<UpdateLinkedCalendarInput>({
    resolver: zodResolver(updateLinkedCalendarSchema),
    defaultValues: {
      scope: calendar.scope,
      tenantId: calendar.tenantId ?? undefined,
      syncEnabled: calendar.syncEnabled,
      colorHex: calendar.colorHex ?? undefined,
    },
  });

  const scope = form.watch('scope');

  useEffect(() => {
    if (open) {
      form.reset({
        scope: calendar.scope,
        tenantId: calendar.tenantId ?? undefined,
        syncEnabled: calendar.syncEnabled,
        colorHex: calendar.colorHex ?? undefined,
      });
    }
  }, [open, calendar, form]);

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await updateCalendar.mutateAsync({
        connectionId,
        calendarId: calendar.id,
        payload: {
          scope: data.scope,
          tenantId: data.scope === 'Tenant' ? (data.tenantId ?? null) : null,
          syncEnabled: data.syncEnabled,
          colorHex: data.colorHex ?? null,
        },
      });
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="settings-integrations-scope-dialog">
        <DialogHeader>
          <DialogTitle>{t('integrations.scopeDialog.title')}</DialogTitle>
          <DialogDescription>{calendar.name}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-5">
          <FormFieldSelect
            control={form.control}
            name="scope"
            label={t('integrations.scopeDialog.scopeLabel')}
            options={[
              { value: 'Global', label: t('integrations.scopeDialog.scopeGlobal') },
              { value: 'Tenant', label: t('integrations.scopeDialog.scopeTenant') },
            ]}
            data-testid="settings-integrations-scope-select"
          />
          <p className="text-xs text-muted-foreground -mt-3">
            {scope === 'Global'
              ? t('integrations.scopeDialog.scopeGlobalHint')
              : t('integrations.scopeDialog.scopeTenantHint')}
          </p>

          {scope === 'Tenant' && (
            <FormFieldSelect
              control={form.control}
              name="tenantId"
              label={t('integrations.scopeDialog.tenantLabel')}
              options={organizations.map((org) => ({ value: org.id, label: org.name }))}
              data-testid="settings-integrations-tenant-select"
            />
          )}

          <Controller
            control={form.control}
            name="syncEnabled"
            render={({ field }) => (
              <ToggleSwitch
                id="linked-calendar-sync"
                label={t('integrations.scopeDialog.syncLabel')}
                checked={field.value}
                onCheckedChange={field.onChange}
                data-testid="settings-integrations-sync-switch"
              />
            )}
          />

          <Controller
            control={form.control}
            name="colorHex"
            render={({ field }) => (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  {t('integrations.scopeDialog.colorLabel')}
                </p>
                <div className="flex flex-wrap gap-2" role="radiogroup">
                  {PRESET_HEX_COLORS.map((hex) => (
                    <button
                      key={hex}
                      type="button"
                      role="radio"
                      aria-checked={field.value === hex}
                      aria-label={hex}
                      onClick={() => field.onChange(hex)}
                      className={cn(
                        'size-6 rounded-sm transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        field.value === hex &&
                          'ring-2 ring-offset-2 ring-offset-background ring-foreground scale-110',
                      )}
                      style={{ background: hex }}
                      data-testid={`settings-integrations-color-${hex}`}
                    />
                  ))}
                </div>
              </div>
            )}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateCalendar.isPending}
            >
              {t('integrations.scopeDialog.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={updateCalendar.isPending}
              data-testid="settings-integrations-scope-save"
            >
              {updateCalendar.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('integrations.scopeDialog.saving')}
                </>
              ) : (
                t('integrations.scopeDialog.save')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
